import { useRef, useState } from "react";
import { FileUp, Loader2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

export type Uploaded = { id: string; filename: string; size: number };
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.dwg,.dxf";
const MAX = 3;

export default function PlanUpload({ files, onChange }: { files: Uploaded[]; onChange: (f: Uploaded[]) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pick = async (list: FileList | null) => {
    if (!list?.length) return;
    setError("");
    const chosen = Array.from(list).slice(0, MAX - files.length);
    const big = chosen.find((f) => f.size > 20 * 1024 * 1024);
    if (big) return setError(`${big.name} is larger than 20MB.`);
    setBusy(true);
    const added: Uploaded[] = [];
    for (const f of chosen) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads", fd);
        added.push(data);
      } catch (e) {
        setError(`${f.name}: ${apiError(e)}`);
      }
    }
    onChange([...files, ...added]);
    setBusy(false);
    if (input.current) input.current.value = "";
  };

  return (
    <div className="sm:col-span-2" data-testid="plan-upload">
      <button
        type="button" disabled={busy || files.length >= MAX} onClick={() => input.current?.click()}
        data-testid="plan-upload-button"
        className="flex w-full items-center justify-center gap-2 border border-dashed border-slate-600 px-3 py-3 text-sm text-slate-300 transition-colors hover:border-brand-500 hover:text-brand-400 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
        {busy ? "Uploading…" : files.length >= MAX ? "Maximum 3 files attached" : "Attach plans or specs (PDF, image, DWG/DXF · up to 3 × 20MB)"}
      </button>
      <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => pick(e.target.files)} data-testid="plan-upload-input" />
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between bg-[#161920] px-3 py-1.5 text-xs text-slate-300" data-testid="plan-upload-item">
              <span className="truncate">{f.filename} · {(f.size / 1024 / 1024).toFixed(1)}MB</span>
              <button type="button" onClick={() => onChange(files.filter((x) => x.id !== f.id))} aria-label="Remove file" data-testid="plan-upload-remove"><X className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="mt-1 text-xs text-red-400" data-testid="plan-upload-error">{error}</p>}
    </div>
  );
}
